import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Bell,
  Calendar,
  Filter,
  Loader2,
  MapPin,
  Moon,
  Search as SearchIcon,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvent,
} from 'react-leaflet';
import type { LatLngBounds } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { MainLayout } from '../layouts/MainLayout';
import { useAvailablePlaces, usePlacesSummary } from '../hooks/useApi';
import { usePageMeta } from '../hooks/usePageMeta';
import { formatShortDate } from '../utils/dates';
import { trackSearch } from '../lib/posthog';

function toIso(date: Date | undefined): string {
  if (!date) return '';
  return date.toISOString().slice(0, 10);
}

function fromIso(iso: string): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

type SortMode = 'relevance' | 'name' | 'sites';

interface ParkSearchResult {
  placeId: string;
  placeName: string;
  sourceName: string;
  latitude: number;
  longitude: number;
  facilityCount: number;
  siteCount: number;
  maxOccupancy: number | null;
  hasAvailabilityInRange: boolean | null;
  imageUrl: string | null;
  facilityNames: string[];
}

function hashString(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function gradientFromName(name: string): { from: string; to: string } {
  const palette = [
    ['#134e4a', '#0f766e'],
    ['#14532d', '#15803d'],
    ['#334155', '#0f766e'],
    ['#854d0e', '#b45309'],
    ['#1e3a8a', '#0ea5e9'],
    ['#4c1d95', '#7c3aed'],
    ['#7f1d1d', '#dc2626'],
  ] as const;
  const pair = palette[hashString(name) % palette.length];
  return { from: pair[0], to: pair[1] };
}

// Continental US bounding box used as the default map view.
const CONUS_BOUNDS: [[number, number], [number, number]] = [
  [24.5, -125],
  [49.5, -66.5],
];

function percentile(sorted: number[], p: number): number {
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

function fitBoundsForPoints(points: ParkSearchResult[]): [[number, number], [number, number]] {
  const lats = points.map((p) => p.latitude).sort((a, b) => a - b);
  const lons = points.map((p) => p.longitude).sort((a, b) => a - b);

  // For large result sets, trim outliers (e.g. Alaska/Hawaii/territories)
  const useRobust = points.length > 50;
  const minLat = useRobust ? percentile(lats, 0.02) : lats[0];
  const maxLat = useRobust ? percentile(lats, 0.98) : lats[lats.length - 1];
  const minLon = useRobust ? percentile(lons, 0.02) : lons[0];
  const maxLon = useRobust ? percentile(lons, 0.98) : lons[lons.length - 1];

  const latPad = Math.max(0.5, (maxLat - minLat) * 0.12);
  const lonPad = Math.max(0.5, (maxLon - minLon) * 0.12);

  return [
    [minLat - latPad, minLon - lonPad],
    [maxLat + latPad, maxLon + lonPad],
  ];
}

function SearchMapController({
  points,
  selectedPlaceId,
  fitBoundsToken,
}: {
  points: ParkSearchResult[];
  selectedPlaceId: string | null;
  fitBoundsToken: number;
}) {
  const map = useMap();
  const hasInitialFit = useRef(false);
  const lastTokenRef = useRef(fitBoundsToken);

  // Fit map bounds only on initial data load and on explicit search submission
  // (fitBoundsToken change). Filter/typing changes should NOT reset the map.
  useEffect(() => {
    const isNewToken = fitBoundsToken !== lastTokenRef.current;
    lastTokenRef.current = fitBoundsToken;

    if (hasInitialFit.current && !isNewToken) return;

    if (points.length === 0) return;

    hasInitialFit.current = true;

    if (points.length === 1) {
      map.setView([points[0].latitude, points[0].longitude], 9);
      return;
    }

    map.fitBounds(fitBoundsForPoints(points), { padding: [28, 28], maxZoom: 8 });
  }, [map, points, fitBoundsToken]);

  useEffect(() => {
    if (!selectedPlaceId) {
      return;
    }
    const selected = points.find(
      (point) => point.placeId === selectedPlaceId
    );
    if (!selected) {
      return;
    }
    map.flyTo([selected.latitude, selected.longitude], Math.max(8, map.getZoom()), {
      duration: 0.35,
    });
  }, [map, points, selectedPlaceId]);

  return null;
}

function MapClickClearController(props: {
  enabled: boolean;
  onClear: () => void;
}) {
  useMapEvent('click', () => {
    if (props.enabled) {
      props.onClear();
    }
  });
  return null;
}

function MapBoundsTracker({ onBoundsChange }: { onBoundsChange: (bounds: LatLngBounds) => void }) {
  const map = useMap();

  useEffect(() => {
    onBoundsChange(map.getBounds());
  }, [map, onBoundsChange]);

  useMapEvent('moveend', () => {
    onBoundsChange(map.getBounds());
  });

  return null;
}

export function SearchPage() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/search' });
  const { data: placeSummaries, isLoading, error } = usePlacesSummary();

  usePageMeta({
    title: 'Search Campsites | HellaCamping',
    description: 'Search for available campsites across state and national parks nationwide.',
    canonicalPath: '/search',
  });

  const [location, setLocation] = useState(searchParams.location ?? '');
  const [startDate, setStartDate] = useState(searchParams.startDate ?? '');
  const [guests, setGuests] = useState<number>(searchParams.guests ?? 2);
  const [nights, setNights] = useState<number>(searchParams.nights ?? 2);
  const [sortMode, setSortMode] = useState<SortMode>('relevance');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [unavailableOnly, setUnavailableOnly] = useState(false);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const calendarToday = useMemo(() => new Date(), []);

  const arrivalDate = fromIso(startDate);
  const endDate = useMemo(() => {
    if (!arrivalDate) return '';
    const d = new Date(arrivalDate);
    d.setDate(d.getDate() + nights - 1);
    return toIso(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, nights]);

  const handleArrivalSelect = (day: Date | undefined) => {
    setStartDate(toIso(day));
    setIsCalendarOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setIsCalendarOpen(false);
      }
    };
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCalendarOpen]);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const [hoveredPlaceId, setHoveredPlaceId] = useState<string | null>(null);
  const [mapBounds, setMapBounds] = useState<LatLngBounds | null>(null);
  const [fitBoundsToken, setFitBoundsToken] = useState(0);
  const [visibleCount, setVisibleCount] = useState(20);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef<string | null>(null);

  const cardByOriginPlaceId = useMemo(
    () => new Map<string, HTMLDivElement | null>(),
    []
  );

  // Perform pending scroll after React renders the new visible items.
  useEffect(() => {
    const placeId = pendingScrollRef.current;
    if (!placeId) return;
    const el = cardByOriginPlaceId.get(placeId);
    if (!el || !listContainerRef.current) return;
    pendingScrollRef.current = null;
    // Scroll within the fixed-height container, not the page.
    const container = listContainerRef.current;
    const elTop = el.offsetTop - container.offsetTop;
    const elHeight = el.offsetHeight;
    const containerHeight = container.clientHeight;
    container.scrollTo({
      top: elTop - (containerHeight - elHeight) / 2,
      behavior: 'smooth',
    });
  });

  const scrollToPlaceId = (placeId: string) => {
    // Ensure the card is rendered by expanding the visible list if needed.
    const idx = visibleResults.findIndex((r) => r.placeId === placeId);
    if (idx >= 0 && idx >= visibleCount) {
      setVisibleCount(idx + 1);
    }
    // Mark for scrolling — the useEffect after render will do the actual scroll.
    pendingScrollRef.current = placeId;

    // If the card is already rendered, the state didn't change so the effect
    // above won't re-fire from a React re-render. Handle that case here.
    const el = cardByOriginPlaceId.get(placeId);
    if (el && listContainerRef.current) {
      requestAnimationFrame(() => {
        if (!pendingScrollRef.current) return;
        pendingScrollRef.current = null;
        const container = listContainerRef.current;
        if (!container) return;
        const elTop = el.offsetTop - container.offsetTop;
        const elHeight = el.offsetHeight;
        const containerHeight = container.clientHeight;
        container.scrollTo({
          top: elTop - (containerHeight - elHeight) / 2,
          behavior: 'smooth',
        });
      });
    }
  };

  const clearSelection = () => {
    setSelectedPlaceId(null);
    setHoveredPlaceId(null);
  };

  useEffect(() => {
    setLocation(searchParams.location ?? '');
    setStartDate(searchParams.startDate ?? '');
    setGuests(searchParams.guests ?? 2);
    setNights(searchParams.nights ?? 2);
  }, [
    searchParams.guests,
    searchParams.location,
    searchParams.nights,
    searchParams.startDate,
  ]);

  const hasValidDateRange =
    startDate.trim().length > 0 &&
    endDate.trim().length > 0;

  const { data: availablePlaceIds } = useAvailablePlaces(
    {
      startDate: hasValidDateRange ? startDate : endDate,
      endDate: hasValidDateRange ? endDate : startDate,
      minNights: nights > 1 ? nights : undefined,
    },
    hasValidDateRange
  );

  const availablePlaceIdSet = useMemo(
    () => new Set<string>(availablePlaceIds ?? []),
    [availablePlaceIds]
  );

  const sourceNames = useMemo(() => {
    if (!placeSummaries) return [];
    return [...new Set(placeSummaries.map((p) => p.sourceName))].sort((a, b) => a.localeCompare(b));
  }, [placeSummaries]);

  const results = useMemo<ParkSearchResult[]>(() => {
    if (!placeSummaries) {
      return [];
    }

    const locationNeedle = location.trim().toLowerCase();

    const output: ParkSearchResult[] = [];
    for (const summary of placeSummaries) {
      if (sourceFilter !== 'all' && summary.sourceName !== sourceFilter) {
        continue;
      }

      if (locationNeedle.length > 0) {
        const placeText = `${summary.placeName} ${summary.sourceName}`.toLowerCase();
        let matches = placeText.includes(locationNeedle);
        if (!matches) {
          for (const fname of summary.facilityNames) {
            if (fname.toLowerCase().includes(locationNeedle)) {
              matches = true;
              break;
            }
          }
        }
        if (!matches) {
          continue;
        }
      }

      if (guests > 0 && summary.maxOccupancy !== null && summary.maxOccupancy < guests) {
        continue;
      }

      const hasAvailabilityInRange = hasValidDateRange
        ? availablePlaceIdSet.has(summary.placeId)
        : null;

      if (unavailableOnly && hasValidDateRange && hasAvailabilityInRange) {
        continue;
      }

      output.push({
        placeId: summary.placeId,
        placeName: summary.placeName,
        sourceName: summary.sourceName,
        latitude: summary.latitude,
        longitude: summary.longitude,
        facilityCount: summary.facilityCount,
        siteCount: summary.siteCount,
        maxOccupancy: summary.maxOccupancy,
        hasAvailabilityInRange,
        imageUrl: summary.imageUrl,
        facilityNames: summary.facilityNames,
      });
    }

    return output.sort((a, b) => {
      if (sortMode === 'name') {
        return a.placeName.localeCompare(b.placeName);
      }
      if (sortMode === 'sites') {
        return b.siteCount - a.siteCount;
      }

      const aScore =
        (locationNeedle.length > 0 && a.placeName.toLowerCase().includes(locationNeedle)
          ? 42
          : 0) + a.siteCount;
      const bScore =
        (locationNeedle.length > 0 && b.placeName.toLowerCase().includes(locationNeedle)
          ? 42
          : 0) + b.siteCount;
      return bScore - aScore;
    });
  }, [
    availablePlaceIdSet,
    placeSummaries,
    guests,
    hasValidDateRange,
    location,
    sortMode,
    sourceFilter,
    unavailableOnly,
  ]);

  // Reset visible count and stale selection when the result set changes
  // (e.g. new search). Must NOT depend on selectedPlaceId — otherwise
  // clicking a map dot resets visibleCount and removes the scroll target.
  useEffect(() => {
    setVisibleCount(20);
    if (results.length === 0) {
      setSelectedPlaceId(null);
    } else {
      setSelectedPlaceId((current) =>
        current !== null && !results.some((row) => row.placeId === current)
          ? null
          : current,
      );
    }
  }, [results]);

  const visibleResults = useMemo(() => {
    if (!mapBounds) return results;
    return results.filter((r) =>
      mapBounds.contains([r.latitude, r.longitude])
    );
  }, [results, mapBounds]);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    trackSearch({
      location: location.trim() || undefined,
      startDate: startDate || undefined,
      nights: Number.isFinite(nights) && nights >= 1 ? nights : undefined,
      guests: Number.isFinite(guests) && guests > 0 ? guests : undefined,
    });
    setFitBoundsToken((t) => t + 1);
    navigate({
      to: '/search',
      search: {
        location: location.trim().length > 0 ? location.trim() : undefined,
        startDate: startDate || undefined,
        guests: Number.isFinite(guests) && guests > 0 ? guests : undefined,
        nights: Number.isFinite(nights) && nights >= 1 ? nights : undefined,
      },
    });
  };

  const handleNotify = (placeId: string) => {
    navigate({
      to: '/park/$id',
      params: { id: String(placeId) },
      search: {
        startDate: startDate || undefined,
        nights: Number.isFinite(nights) && nights >= 1 ? nights : undefined,
        guests: Number.isFinite(guests) && guests > 0 ? guests : undefined,
      },
    });
  };

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <form
            onSubmit={handleSearchSubmit}
            className="grid grid-cols-1 lg:grid-cols-[2fr_auto_auto_auto] gap-2"
          >
            <label className="relative block">
              <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
              <input
                type="text"
                value={location}
                onChange={(event) => setLocation(event.target.value)}
                placeholder="Where to?"
                className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>

            <div className="flex items-center gap-1.5">
              <div className="relative" ref={calendarRef}>
                <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-3.5 pointer-events-none z-10" />
                <button
                  type="button"
                  onClick={() => setIsCalendarOpen((o) => !o)}
                  className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-left outline-none focus:ring-2 focus:ring-emerald-500 whitespace-nowrap"
                >
                  <span className={startDate ? 'text-gray-900' : 'text-gray-400'}>
                    {startDate
                      ? `${formatShortDate(startDate)} · ${nights} night${nights !== 1 ? 's' : ''}`
                      : 'Arrival date'}
                  </span>
                </button>

                {isCalendarOpen && (
                  <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-30 p-4 w-max max-w-[calc(100vw-2rem)]">
                    <DayPicker
                      mode="single"
                      selected={arrivalDate}
                      onSelect={handleArrivalSelect}
                      numberOfMonths={2}
                      classNames={{ months: 'flex flex-col sm:flex-row gap-4' }}
                      disabled={{ before: calendarToday }}
                      defaultMonth={arrivalDate ?? calendarToday}
                    />
                  </div>
                )}
              </div>

              <label className="relative block">
                <Moon className="w-4 h-4 text-gray-400 absolute left-2.5 top-3.5" />
                <input
                  type="number"
                  min={1}
                  max={14}
                  value={nights}
                  onChange={(event) => {
                    const parsed = Number.parseInt(event.target.value, 10);
                    setNights(Number.isFinite(parsed) && parsed >= 1 ? parsed : 1);
                  }}
                  className="w-20 rounded-lg border border-gray-300 bg-white pl-8 pr-2 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </label>
            </div>

            <label className="relative block">
              <Users className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
              <input
                type="number"
                min={1}
                max={20}
                value={guests}
                onChange={(event) => {
                  const parsed = Number.parseInt(event.target.value, 10);
                  setGuests(Number.isFinite(parsed) ? parsed : 1);
                }}
                className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>

            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-white hover:bg-emerald-700"
            >
              <SearchIcon className="w-4 h-4" />
              Search
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1 text-sm text-gray-700">
              <Filter className="w-3.5 h-3.5" />
              Filters
            </span>

            <select
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value as SortMode)}
              className="rounded-full border border-gray-300 px-3 py-1 text-sm text-gray-700 bg-white"
            >
              <option value="relevance">Sort: Relevance</option>
              <option value="name">Sort: Name</option>
              <option value="sites">Sort: Most Sites</option>
            </select>

            <select
              value={sourceFilter}
              onChange={(event) => setSourceFilter(event.target.value)}
              className="rounded-full border border-gray-300 px-3 py-1 text-sm text-gray-700 bg-white"
            >
              <option value="all">All sources</option>
              {sourceNames.map((sourceName) => (
                <option key={sourceName} value={sourceName}>
                  {sourceName}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => setUnavailableOnly((value) => !value)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm ${
                unavailableOnly
                  ? 'border-amber-400 bg-amber-50 text-amber-700'
                  : 'border-gray-300 text-gray-700'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Only show no openings
            </button>

            {selectedPlaceId !== null && (
              <button
                type="button"
                onClick={clearSelection}
                className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
              >
                Clear selection
              </button>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="rounded-xl border border-gray-200 bg-white p-16 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-2" />
            <p className="text-gray-600">Loading search results...</p>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
            Failed to load search results: {error.message}
          </div>
        )}

        {!isLoading && !error && (
          <div className="grid xl:grid-cols-[minmax(0,1.35fr)_minmax(420px,1fr)] gap-4">
            <div className="space-y-3">
              <p className="text-sm text-gray-700 px-1">
                <span className="font-semibold text-gray-900">{visibleResults.length}</span> parks
                {visibleResults.length < results.length && (
                  <span className="text-gray-500"> ({visibleResults.length} in view)</span>
                )}
              </p>

              <div ref={listContainerRef} className="xl:h-[72vh] xl:overflow-y-auto space-y-3">
                {visibleResults.length === 0 && (
                  <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
                  <p className="text-gray-700 font-medium">No parks match this search.</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Try a broader location, fewer guests, or zoom out on the map.
                  </p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-3">
                {visibleResults.slice(0, visibleCount).map((row) => {
                  const gradient = gradientFromName(row.placeName);
                  const isSelected = row.placeId === selectedPlaceId;
                  const isHovered = row.placeId === hoveredPlaceId;

                  return (
                    <div
                      key={row.placeId}
                      ref={(node) => {
                        cardByOriginPlaceId.set(row.placeId, node);
                      }}
                      className={`rounded-xl border bg-white shadow-sm overflow-hidden transition-colors ${
                        isSelected
                          ? 'border-emerald-400'
                          : isHovered
                            ? 'border-slate-400'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                      onMouseEnter={() => setHoveredPlaceId(row.placeId)}
                      onMouseLeave={() =>
                        setHoveredPlaceId((value) =>
                          value === row.placeId ? null : value
                        )
                      }
                      onClick={() => setSelectedPlaceId(row.placeId)}
                    >
                      <div className="h-36 relative overflow-hidden bg-gray-100">
                        <div
                          className="absolute inset-0"
                          style={{
                            background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
                          }}
                        />
                        {row.imageUrl ? (
                          <img
                            src={row.imageUrl}
                            alt={row.placeName}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : null}
                        <div className="absolute inset-0 bg-black/10" />
                      </div>
                      <div className="p-3 space-y-2">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">
                            {row.sourceName}
                          </p>
                          <h3 className="font-semibold text-gray-900">{row.placeName}</h3>
                        </div>

                        <div className="text-sm text-gray-600 space-y-0.5">
                          <p>{row.facilityCount.toLocaleString()} campgrounds</p>
                          <p>{row.siteCount.toLocaleString()} sites</p>
                          <p>
                            {row.maxOccupancy !== null
                              ? `up to ${row.maxOccupancy} guests`
                              : 'guest capacity varies by site'}
                          </p>
                        </div>

                        {hasValidDateRange ? (
                          <p
                            className={`text-xs rounded-md px-2 py-1 inline-flex ${
                              row.hasAvailabilityInRange
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-amber-50 text-amber-700'
                            }`}
                          >
                            {row.hasAvailabilityInRange
                              ? 'Recent openings detected in selected dates'
                              : 'No openings detected in selected dates'}
                          </p>
                        ) : (
                          <p className="text-xs rounded-md px-2 py-1 inline-flex bg-slate-100 text-slate-600">
                            Add dates to check openings
                          </p>
                        )}

                        <div className="flex items-center gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() =>
                              navigate({
                                to: '/park/$id',
                                params: { id: String(row.placeId) },
                                search: {
                                  startDate: startDate || undefined,
                                  nights: Number.isFinite(nights) && nights >= 1 ? nights : undefined,
                                  guests: Number.isFinite(guests) && guests > 0 ? guests : undefined,
                                },
                              })
                            }
                            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            View Park
                          </button>
                          <button
                            type="button"
                            onClick={() => handleNotify(row.placeId)}
                            className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-700"
                          >
                            <Bell className="w-3.5 h-3.5" />
                            Notify Me
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {visibleCount < visibleResults.length && (
                <button
                  type="button"
                  onClick={() => setVisibleCount((n) => n + 20)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm"
                >
                  Load more ({(visibleResults.length - visibleCount).toLocaleString()} remaining)
                </button>
              )}
              </div>
            </div>

            <div className="xl:sticky xl:top-20 h-[72vh] rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
              <MapContainer
                className="h-full w-full"
                bounds={CONUS_BOUNDS}
                minZoom={3}
                scrollWheelZoom={true}
                preferCanvas={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                />

                <SearchMapController
                  points={results}
                  selectedPlaceId={selectedPlaceId}
                  fitBoundsToken={fitBoundsToken}
                />

                <MapClickClearController
                  enabled={selectedPlaceId !== null}
                  onClear={clearSelection}
                />

                <MapBoundsTracker onBoundsChange={setMapBounds} />

                {results.map((row) => (
                  <CircleMarker
                    key={row.placeId}
                    center={[row.latitude, row.longitude]}
                    radius={
                      row.placeId === selectedPlaceId
                        ? 9
                        : row.placeId === hoveredPlaceId
                          ? 8
                          : 6
                    }
                    pathOptions={{
                      // Prevent marker clicks from bubbling into the map click handler (which clears selection).
                      bubblingMouseEvents: false,
                      color:
                        row.placeId === selectedPlaceId
                          ? '#0f172a'
                          : row.hasAvailabilityInRange === false
                            ? '#b45309'
                            : '#059669',
                      fillColor:
                        row.hasAvailabilityInRange === false ? '#f59e0b' : '#10b981',
                      fillOpacity: 0.8,
                      weight: row.placeId === selectedPlaceId ? 2 : 1,
                    }}
                    eventHandlers={{
                      click: (event) => {
                        event.originalEvent?.stopPropagation?.();
                        setSelectedPlaceId(row.placeId);
                        scrollToPlaceId(row.placeId);
                      },
                      mouseover: () => setHoveredPlaceId(row.placeId),
                      mouseout: () =>
                        setHoveredPlaceId((value) =>
                          value === row.placeId ? null : value
                        ),
                    }}
                  >
                    {row.placeId === selectedPlaceId ? (
                      <Tooltip
                        key={`${row.placeId}:selected`}
                        direction="top"
                        offset={[0, -6]}
                        opacity={0.95}
                        permanent={true}
                      >
                        <div className="text-xs">
                          <div className="font-semibold">{row.placeName}</div>
                          <div className="opacity-80">{row.facilityCount.toLocaleString()} campgrounds</div>
                          <div className="opacity-80">
                            {row.siteCount.toLocaleString()} sites
                          </div>
                        </div>
                      </Tooltip>
                    ) : null}
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
